import ImageGallery from 'react-image-gallery';
import Skeleton from 'react-loading-skeleton';
import { UncontrolledTooltip } from 'reactstrap';
import {
  IconButton,
  WhiteArrowBackIcon as arrowBack,
  WhiteArrowNextIcon as arrowNext,
  EditIcon as editIcon,
} from '@/shared/ui';
import EditDeviceStripModal from './editStripModal';
import useDeviceStripImageViewer from './useDeviceStripImageViewer';

export type DeviceStripImageViewerProps = {
  activeButton: any;
  keyRecoil: any;
  ecgDataMap: any;
  reportData?: any;
  timezoneOffset?: any;
};

/**
 * Displays the captured strip images for the selected device event and opens the edit dialog for them. useDeviceStripImageViewer supplies the image list and selection; a skeleton covers strip loading, a gallery with custom arrows shows the images and reports load errors through its own overlay, and a message explains an unavailable strip, including the no-data-zone case which, like a removed event, also disables the edit button.
 *
 * @param props - Viewer props carrying the report data, ECG data map and timezone offset.
 * @returns The rendered strip image gallery with its edit button and, when open, the edit-strip dialog.
 */
const DeviceStripImageViewer = ({
  activeButton,
  keyRecoil,
  ecgDataMap,
  reportData,
  timezoneOffset,
}: DeviceStripImageViewerProps) => {
  const {
    state,
    setState,
    listImages,
    imageRef,
    isLoadingStrip,
    viewSelectedStrip,
    isDeviceEvent,
    onClickEditStrip,
    closeEditStripModal,
    updateEditData,
  } = useDeviceStripImageViewer({ activeButton, keyRecoil });

  return (
    <div className="strip-management-ai-strip-content">
      {isLoadingStrip ? (
        <div className="strip-management-ai-strip-content-skeleton">
          <Skeleton height="100%" />
        </div>
      ) : (
        <>
          <div className="ecg-strip">
            {state.stripImgs?.length ? (
              <div className="strip-limit-width">
                <ImageGallery
                  ref={imageRef}
                  items={listImages}
                  infinite={false}
                  showPlayButton={false}
                  showFullscreenButton={false}
                  showBullets
                  lazyLoad
                  disableKeyDown
                  slideDuration={250}
                  startIndex={0}
                  thumbnailPosition="bottom"
                  renderLeftNav={(onClick, disabled) => (
                    <button
                      id="strips-device-gallery-prev-btn"
                      className="image-gallery-custom-nav left"
                      disabled={disabled}
                      onClick={onClick}
                    >
                      <img src={arrowBack} alt="back" />
                    </button>
                  )}
                  renderRightNav={(onClick, disabled) => (
                    <button
                      id="strips-device-gallery-next-btn"
                      className="image-gallery-custom-nav right"
                      disabled={disabled}
                      onClick={onClick}
                    >
                      <img src={arrowNext} alt="next" />
                    </button>
                  )}
                  onImageLoad={() => {
                    setState({ isLoading: false, isLoadedImageError: false });
                  }}
                  onImageError={(event) => {
                    if (event.type === 'error') {
                      setState({ isLoading: false, isLoadedImageError: true });
                    }
                  }}
                />
                {state.isLoading ? (
                  <div className="loading-thumbnail">
                    <Skeleton height="101%" />
                  </div>
                ) : state.isLoadedImageError ? (
                  <div className="loading-thumbnail --error">
                    There is an error while loading ECG strips
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="no-ecg-strip-container">
                {/* <img className="no-ecg-strip-icon" src={noDataIcon} alt={'No data icon'} /> */}
                <div className="no-ecg-strip-text">
                  {viewSelectedStrip?.isNoEcgData
                    ? 'This strip image is currently unavailable because patient captured event in no data zone'
                    : 'The strip image is currently unavailable'}
                </div>
              </div>
            )}
          </div>
          {viewSelectedStrip?.id && (
            <div className="edit-ecg-strip-button-wrapper" id="edit-strip-btn-id">
              <IconButton
                id="device-event-edit-strip-btn"
                className="edit-ecg-strip-button"
                isFilledGradient
                ariaLabel={
                  viewSelectedStrip?.isNoEcgData
                    ? 'Cannot edit events with unavailable strips'
                    : 'Edit strip'
                }
                disabled={viewSelectedStrip?.isRemoved || viewSelectedStrip?.isNoEcgData}
                iconComponent={<img src={editIcon} alt="" />}
                onClick={(e) => {
                  e.preventDefault();
                  onClickEditStrip();
                }}
              />
              <UncontrolledTooltip
                className="custom-tooltip"
                target="edit-strip-btn-id"
                placement="bottom"
                delay={{ show: 0, hide: 0 }}
                hideArrow
              >
                {viewSelectedStrip?.isNoEcgData
                  ? 'Cannot edit events with unavailable strips'
                  : 'Edit strip'}
              </UncontrolledTooltip>
            </div>
          )}
        </>
      )}
      {state.isVisibleEditAiStripModal && (
        <EditDeviceStripModal
          visible={state.isVisibleEditAiStripModal}
          handleClickCloseButton={() => closeEditStripModal()}
          data={state.selectedData}
          originalData={state.originalSelectedData}
          updateEditData={updateEditData}
          ecgDataMap={ecgDataMap}
          timezoneOffset={timezoneOffset}
          ecgViewerConfig={reportData?.holterProfile?.reportConfiguration?.ecgViewer}
          isDeviceEvent={isDeviceEvent}
        />
      )}
    </div>
  );
};

export default DeviceStripImageViewer;
